import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { fromEvent } from "rxjs";

type NetworkStatus = "online" | "offline";

@Component({
  selector: "pms-network-status",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div
        class="network-status"
        [class.network-status--offline]="status() === 'offline'"
        [class.network-status--online]="status() === 'online'"
        role="status"
        aria-live="polite"
      >
        <span class="network-status__dot" aria-hidden="true"></span>
        <span>{{ message() }}</span>
      </div>
    }
  `,
  styles: `
    :host {
      pointer-events: none;
      position: fixed;
      left: 3.5rem;
      bottom: 1rem;
      z-index: 10000;
    }

    .network-status {
      align-items: center;
      border-radius: 9999px;
      box-shadow: 0 12px 28px rgb(15 23 42 / 28%);
      display: flex;
      font-size: 1.125rem;
      font-weight: 700;
      gap: 0.75rem;
      letter-spacing: 0.01em;
      padding: 0.75rem 1.25rem;
      text-transform: uppercase;
    }

    .network-status--offline {
      background: #991b1b;
      color: #ffffff;
    }

    .network-status--online {
      background: #166534;
      color: #ffffff;
    }

    .network-status__dot {
      background: currentColor;
      border-radius: 9999px;
      display: inline-block;
      height: 0.75rem;
      width: 0.75rem;
    }
  `
})
export class NetworkStatusComponent {
  readonly #destroyRef = inject(DestroyRef);

  readonly status = signal<NetworkStatus>(this.#getInitialStatus());
  readonly visible = signal<boolean>(this.status() === "offline");
  readonly message = computed<string>(() => {
    switch (this.status()) {
      case "offline": {
        return "Offline";
      }
      case "online": {
        return "Online";
      }
    }
  });

  #onlineNotificationTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#clearOnlineNotificationTimer();
    });

    if (typeof window === "undefined") {
      return;
    }

    fromEvent(window, "offline")
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#setStatus("offline");
      });

    fromEvent(window, "online")
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#setStatus("online");
      });
  }

  #getInitialStatus(): NetworkStatus {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "offline";
    }

    return "online";
  }

  #setStatus(status: NetworkStatus): void {
    this.#clearOnlineNotificationTimer();
    this.status.set(status);
    this.visible.set(true);

    if (status === "offline") {
      return;
    }

    this.#onlineNotificationTimer = setTimeout(() => {
      this.visible.set(this.status() === "offline");
    }, 3000);
  }

  #clearOnlineNotificationTimer(): void {
    if (!this.#onlineNotificationTimer) {
      return;
    }

    clearTimeout(this.#onlineNotificationTimer);
    this.#onlineNotificationTimer = undefined;
  }
}
